'use client'

import { Field, FieldContent, FieldDescription, FieldLabel, FieldLegend, FieldSet, FieldTitle } from '@ui/base/field'
import { RadioGroup, RadioGroupItem } from '@ui/base/radio-group'
import { useFormContext } from 'react-hook-form'

import { type FulfillmentFormValues } from '../model/types'
import { CityChooser } from './city-chooser'

const areaOptions = [
  ['all', 'Во все города России', 'Работаем с клиентами по всей стране.'],
  ['selected', 'В выбранные города', 'Укажите города, в которых доступна услуга.'],
] as const

type Props = {
  /** Схема работы, у которой своя география: FBS и DBS ведут списки городов раздельно. */
  type: 'fbs' | 'dbs'
  label: string
}

/** География схемы работы: вся страна или перечень городов. */
export const ServiceAreaField = ({ type, label }: Props) => {
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FulfillmentFormValues>()
  const name = type === 'fbs' ? 'fbsArea' : 'dbsArea'
  const area = watch(name)

  /**
   * Записываем географию целиком, а не отдельное поле внутри неё: требование «выберите хотя бы
   * один город» проверяется у всей географии, и правка по вложенному пути оставила бы красную
   * подпись висеть после того, как город уже добавлен.
   */
  const changeArea = (next: Partial<FulfillmentFormValues['fbsArea']>) =>
    setValue(name, { ...area, ...next }, { shouldValidate: true, shouldDirty: true })

  return (
    <FieldSet>
      <FieldLegend variant="section">География {label}</FieldLegend>
      <RadioGroup
        value={area.mode}
        onValueChange={(mode) => changeArea({ mode: mode as FulfillmentFormValues['fbsArea']['mode'] })}
        aria-label={`География ${label}`}
      >
        {areaOptions.map(([value, optionLabel, optionDescription]) => {
          const optionId = `fulfillment-${type}-area-${value}`
          return (
            <div key={value} className="space-y-3">
              <FieldLabel htmlFor={optionId}>
                <Field orientation="horizontal">
                  <RadioGroupItem
                    id={optionId}
                    size="lg"
                    value={value}
                    aria-labelledby={`${optionId}-title`}
                    aria-describedby={`${optionId}-description`}
                  />
                  <FieldContent>
                    <FieldTitle id={`${optionId}-title`}>
                      {optionLabel} для {label}
                    </FieldTitle>
                    <FieldDescription id={`${optionId}-description`}>{optionDescription}</FieldDescription>
                  </FieldContent>
                </Field>
              </FieldLabel>
              {value === 'selected' && area.mode === 'selected' && (
                <CityChooser
                  type={type}
                  cities={area.cities}
                  onChange={(cities) => changeArea({ cities })}
                  error={errors[name]?.message}
                />
              )}
            </div>
          )
        })}
      </RadioGroup>
    </FieldSet>
  )
}
